import { branding } from '@guridosdrones/shared';
import { InstagramIcon, WhatsAppIcon } from '@guridosdrones/ui';
import { CirclePlay, ExternalLink, Music2, Phone } from 'lucide-react';
import { FadeIn, StaggerContainer, StaggerItem } from '@/components';

export function Contact() {
  const handleWhatsAppClick = () => {
    window.open(branding.contact.whatsapp, '_blank', 'noopener,noreferrer');
  };

  const socialLinks = [
    {
      name: 'Instagram',
      url: branding.social.instagram,
      label: 'Dicas Diárias',
      code: 'CH_01',
      icon: InstagramIcon,
    },
    {
      name: 'YouTube',
      url: branding.social.youtube,
      label: 'Conteúdo Completo',
      code: 'CH_02',
      icon: CirclePlay,
    },
    {
      name: 'TikTok',
      url: branding.social.tiktok,
      label: 'Vídeos Rápidos',
      code: 'CH_03',
      icon: Music2,
    },
  ];

  return (
    <section id="contact" className="section-shell bg-background transition-colors duration-300">
      {/* Background glow */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,var(--tw-gradient-stops))] from-primary/8 via-background to-background" />

      {/* Grid pattern */}
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-size-[48px_48px] opacity-30 dark:opacity-60" />

      <div className="section-container">
        {/* Header */}
        <div className="section-header">
          <FadeIn direction="up" distance={20}>
            <p className="mb-3 font-mono text-xs tracking-[0.25em] text-primary/70 uppercase">
              Canal de Comunicação
            </p>
            <h2 className="section-title mb-4">Entre em Contato</h2>
            <p className="section-description">
              Estamos prontos para tirar suas dúvidas e te ajudar a decolar na carreira de piloto
              agrícola.
            </p>
          </FadeIn>
        </div>

        <div className="grid gap-7 sm:gap-8 lg:grid-cols-5 lg:gap-10">
          {/* WhatsApp Hero Card */}
          <FadeIn className="lg:col-span-3 h-full" direction="left" distance={28} scale>
            <div className="group relative h-full overflow-hidden border border-primary/20 bg-card p-6 shadow-2xl shadow-primary/5 dark:border-primary/15 sm:p-10 lg:p-12">
              {/* Corner brackets */}
              <svg
                className="absolute top-3 left-3 h-4 w-4 text-primary/30"
                viewBox="0 0 20 20"
                fill="none"
                role="img"
                aria-label="Decoração"
              >
                <path d="M0 6V0h6" stroke="currentColor" strokeWidth="1" />
              </svg>
              <svg
                className="absolute top-3 right-3 h-4 w-4 text-primary/30"
                viewBox="0 0 20 20"
                fill="none"
                role="img"
                aria-label="Decoração"
              >
                <path d="M20 6V0h-6" stroke="currentColor" strokeWidth="1" />
              </svg>

              {/* Pulse Effect */}
              <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-primary/5 blur-[100px] transition-all group-hover:bg-primary/10" />

              <div className="relative z-10 flex flex-col h-full justify-between gap-8">
                <div className="flex flex-col gap-6">
                  <div className="inline-flex h-14 w-14 items-center justify-center border border-primary/30 bg-primary/10 shadow-lg shadow-primary/10 sm:h-16 sm:w-16">
                    <WhatsAppIcon className="h-8 w-8 text-primary" />
                  </div>
                  <div>
                    <h3 className="mb-2 text-2xl font-bold text-foreground sm:text-3xl">
                      WhatsApp Oficial
                    </h3>
                    <p className="text-base text-muted-foreground sm:text-lg">
                      O canal mais rápido para falar com o Guri. Tire dúvidas sobre cursos, agenda e
                      equipamentos agora mesmo.
                    </p>
                  </div>
                </div>

                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-3 font-mono text-xs font-medium tracking-widest text-primary uppercase">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
                    </span>
                    Online Agora
                  </div>
                  <button
                    type="button"
                    onClick={handleWhatsAppClick}
                    className="group/btn h-12 w-full inline-flex items-center justify-center gap-3 border border-primary/40 bg-primary/10 font-mono text-sm font-bold tracking-widest text-primary uppercase transition-all hover:border-primary/70 hover:bg-primary/20 sm:h-14 sm:text-base"
                  >
                    <WhatsAppIcon className="h-5 w-5" />
                    Chamar no WhatsApp
                  </button>
                </div>
              </div>
            </div>
          </FadeIn>

          {/* Social Grid */}
          <div className="lg:col-span-2 flex flex-col gap-6">
            <StaggerContainer staggerDelay={0.09}>
              {socialLinks.map((social) => (
                <StaggerItem key={social.name}>
                  <a
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative flex items-center gap-4 overflow-hidden border border-border bg-card p-5 transition-all hover:border-primary/40 hover:bg-muted/50 dark:border-white/10 sm:gap-6 sm:p-6"
                  >
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center border border-border bg-muted/50 text-muted-foreground transition-colors group-hover:border-primary/30 group-hover:bg-primary/10 group-hover:text-primary dark:border-white/10 dark:bg-white/5">
                      <social.icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h4 className="text-base font-bold text-foreground transition-colors group-hover:text-primary sm:text-lg">
                          {social.name}
                        </h4>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-[9px] tracking-widest text-muted-foreground/40">
                            {social.code}
                          </span>
                          <ExternalLink className="h-3.5 w-3.5 text-muted-foreground/30 group-hover:text-primary/60 transition-colors" />
                        </div>
                      </div>
                      <p className="mt-1 text-sm text-muted-foreground">{social.label}</p>
                    </div>
                  </a>
                </StaggerItem>
              ))}
            </StaggerContainer>

            {/* Direct Contacts */}
            <FadeIn
              direction="up"
              delay={0.15}
              className="mt-auto border border-dashed border-border p-6 dark:border-white/10"
            >
              <h4 className="mb-4 font-mono text-[10px] tracking-[0.2em] text-primary/50 uppercase">
                Outros Canais
              </h4>
              <ul className="flex flex-col gap-4">
                <li>
                  <a
                    href={`tel:${branding.contact.phone}`}
                    className="flex items-center gap-3 text-muted-foreground hover:text-primary transition-colors group"
                  >
                    <div className="flex h-8 w-8 items-center justify-center border border-border bg-muted group-hover:border-primary/30 group-hover:bg-primary/10 transition-colors dark:border-white/10 dark:bg-white/5">
                      <Phone className="h-4 w-4" />
                    </div>
                    <span className="text-sm">{branding.contact.phone}</span>
                  </a>
                </li>
              </ul>
            </FadeIn>
          </div>
        </div>
      </div>
    </section>
  );
}
